import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import React, { useEffect } from 'react';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Card from 'components/card/Card';
import {
  useCreateSymptomMutation,
  useDeleteSymptomMutation,
  useGetSymptomsQuery,
  useToggleSymptomStatusMutation,
  useUpdateSymptomMutation,
} from 'api/symptomsSlice';

const PER_PAGE = 20;

const Symptoms = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [editing, setEditing] = React.useState(null);
  const [name, setName] = React.useState('');
  const [pageLoading, setPageLoading] = React.useState(true);
  const requestKey = `${currentPage}|${searchQuery}`;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const searchBg = useColorModeValue('secondaryGray.300', 'gray.700');
  const searchColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next === searchQuery) return;
      setSearchQuery(next);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  useEffect(() => {
    setPageLoading(true);
  }, [requestKey]);

  const { data, isLoading, isError, isFetching } = useGetSymptomsQuery(
    {
      search: searchQuery,
      page: currentPage,
      per_page: PER_PAGE,
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!isFetching && !isLoading) setPageLoading(false);
  }, [isFetching, isLoading, requestKey, data]);
  const [createSymptom, { isLoading: isCreating }] = useCreateSymptomMutation();
  const [updateSymptom, { isLoading: isUpdating }] = useUpdateSymptomMutation();
  const [deleteSymptom] = useDeleteSymptomMutation();
  const [toggleStatus] = useToggleSymptomStatusMutation();

  const items = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination || {};
  const total = pagination.total || 0;
  const lastPage = pagination.last_page || 1;
  const showShimmer = isLoading || pageLoading;

  const openCreate = () => {
    setEditing(null);
    setName('');
    onOpen();
  };

  const openEdit = (item) => {
    setEditing(item);
    setName(item.name);
    onOpen();
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      if (editing) {
        await updateSymptom({ id: editing.id, name: name.trim() }).unwrap();
        toast({ title: 'Symptom updated', status: 'success', duration: 2500, isClosable: true });
      } else {
        await createSymptom({ name: name.trim() }).unwrap();
        toast({ title: 'Symptom added to library', status: 'success', duration: 2500, isClosable: true });
      }
      onClose();
    } catch (error) {
      toast({
        title: 'Could not save symptom',
        description: error?.data?.message || 'That name may already exist in another format',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Delete this symptom?',
      text: item.name,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53e3e',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteSymptom(item.id).unwrap();
      toast({ title: 'Deleted', status: 'success', duration: 2500, isClosable: true });
    } catch (error) {
      toast({
        title: 'Could not delete',
        description: error?.data?.message || 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box className="nr-page">
      <Card flexDirection="column" w="100%" px="0px">
        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="16px"
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={4}
        >
          <Box>
            <Text color={textColor} fontSize="22px" fontWeight="700">
              Symptom Library
            </Text>
            <Text color={muted} fontSize="sm" mt="4px">
              One official name per symptom. “Headache” and “head ache” become the same item.
            </Text>
          </Box>
          <Flex gap={3} wrap="wrap">
            <InputGroup w={{ base: '100%', md: '280px' }}>
              <InputLeftElement>
                <IconButton bg="inherit" _hover="none" icon={<SearchIcon w="15px" h="15px" />} />
              </InputLeftElement>
              <Input
                variant="search"
                fontSize="sm"
                bg={searchBg}
                color={searchColor}
                borderRadius="30px"
                placeholder="Search symptoms..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </InputGroup>
            <Button leftIcon={<FaPlus />} variant="darkBrand" color="white" borderRadius="70px" onClick={openCreate}>
              Add symptom
            </Button>
          </Flex>
        </Flex>

        <Box px={{ base: '16px', md: '25px' }} pb="24px" overflowX="auto">
          {isError ? (
            <Text color="red.500">Could not load the symptom library.</Text>
          ) : showShimmer ? (
            <Box>
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} h="36px" mb="10px" borderRadius="md" />
              ))}
            </Box>
          ) : items.length === 0 ? (
            <Text color={muted}>No symptoms yet. Existing disease symptoms will import automatically.</Text>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color="gray.400" borderColor={borderColor}>Symptom</Th>
                  <Th color="gray.400" borderColor={borderColor}>Status</Th>
                  <Th color="gray.400" borderColor={borderColor}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((item) => (
                  <Tr key={item.id}>
                    <Td borderColor={borderColor}>
                      <Text fontWeight="700" color={textColor}>{item.name}</Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge
                        colorScheme={item.status === 'active' ? 'green' : 'red'}
                        cursor="pointer"
                        onClick={() => toggleStatus(item.id)}
                      >
                        {item.status}
                      </Badge>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Flex gap={3}>
                        <Icon as={FaEdit} color="brand.500" cursor="pointer" onClick={() => openEdit(item)} />
                        <Icon as={FaTrash} color="red.500" cursor="pointer" onClick={() => handleDelete(item)} />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {lastPage > 1 && (
            <Flex justify="center" align="center" mt="20px" gap={3}>
              <IconButton
                icon={<ChevronLeftIcon />}
                size="sm"
                variant="outline"
                colorScheme="brand"
                isDisabled={currentPage <= 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              />
              <Text fontSize="sm">Page {currentPage} of {lastPage} · {total} symptoms</Text>
              <IconButton
                icon={<ChevronRightIcon />}
                size="sm"
                variant="outline"
                colorScheme="brand"
                isDisabled={currentPage >= lastPage}
                onClick={() => setCurrentPage((page) => Math.min(lastPage, page + 1))}
              />
            </Flex>
          )}
        </Box>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? 'Edit symptom' : 'Add symptom'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color={muted} mb={3}>
              Use one clear name. Different spellings of the same symptom will match this entry.
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Headache"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" isLoading={isCreating || isUpdating} onClick={handleSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Symptoms;
